//App.js
import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom"; 
// import { useAuth } from "@clerk/clerk-react";
import useAuthWithTimeout from "./utils/useAuthWithTimeout";
import { UserProvider } from "./components/UserProvider";
import { Toaster } from "react-hot-toast";
import PageTransition from "./components/PageTransition/PageTransition";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";
import useWishlistSync from "./utils/useWishlistSync";
import ChooseCluster from "./pages/ChooseCluster/ChooseCluster";
import { trackPageView } from "./utils/analytics";
import SeoManager from "./components/Seo/SeoManager";

const LandingPage = lazy(() => import("./pages/LandingPage/Landingpage"));
const Itinerary = lazy(() => import("./pages/itinerary/itinerary"));
const WishList = lazy(() => import("./pages/WishListPage/WishList"));
const YourTrips = lazy(() => import("./pages/yourtrips/yourtrips"));
const ChooseDestination = lazy(() => import("./pages/ChooseDestination/ChooseDestination"));
const SharedItinerary = lazy(() => import("./pages/SharedItinerary/SharedItinerary"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));
const ChoosePlaces = lazy(() => import("./pages/ChoosePlaces/ChoosePlaces"));
const ChooseHotel = lazy(() => import("./pages/ChooseHotel/ChooseHotel"));
const FinalItinerary = lazy(() => import("./pages/FinalItinerary/FinalItinerary"));
const Login = lazy(() => import("./pages/Login/Login"));
const SignUp = lazy(() => import("./pages/SignUp/SignUp"));
const BookTrip = lazy(() => import("./pages/BookTrip/Booktrip"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword/forgotpassword"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy/PrivacyPolicy"));
const DemoItinerary = lazy(() => import("./pages/DemoItinerary/DemoItinerary"));
const AgentPage = lazy(() => import("./pages/AgentPage/AgentPage"));
const ReelToReal = lazy(() => import("./pages/ReelToReal/ReelToReal"));
const AboutUs = lazy(() => import("./pages/AboutUs/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs/ContactUs"));
const SSOCallback = lazy(() => import("./pages/SSOCallback/SSOCallback"));
const BudgetItineraryPage = lazy(() => import("./pages/SurpriseMe/BudgetItineraryPage"));
const TreksPage = lazy(() => import("./pages/Treks/TreksPage"));
const TrekDetailPage = lazy(() => import("./pages/Treks/TrekDetailPage"));


const ProtectedRoute = ({ children }) => {
  const { isLoaded, isSignedIn, degraded, retry } = useAuthWithTimeout(10000);
  const location = useLocation();

  if (!isLoaded && !degraded) {
    return <LoadingScreen />;
  }

  if (degraded && !isSignedIn) {
    // Clerk failed to initialize in a timely manner — show a clear fallback
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h2>Authentication temporarily unavailable</h2>
        <p>We're having trouble connecting to the authentication service.</p>
        <div style={{ marginTop: 16 }}>
          <button onClick={retry} style={{ marginRight: 12 }}>Retry</button>
          <button onClick={() => window.location.replace(`/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`)}>Open Login</button>
        </div>
        <p style={{ marginTop: 12, color: "#666" }}>If the problem persists, try a different network or device.</p>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return children;
};

// Public Only Route Component - Redirect to itinerary if already logged in
const PublicOnlyRoute = ({ children }) => {
  const { isLoaded, isSignedIn, degraded, retry } = useAuthWithTimeout(10000);
  const location = useLocation();

  if (!isLoaded && !degraded) {
    return <LoadingScreen />;
  }

  if (degraded) {
    // Allow public route to render but show a small notice so users know auth is degraded
    return (
      <div>
        <div style={{ padding: 12, background: "#fff7e6", textAlign: "center" }}>
          <strong>Auth service slow</strong> — authentication may take longer than usual. <button onClick={retry}>Retry</button>
        </div>
        {children}
      </div>
    );
  }

  if (isSignedIn) {
    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get("returnTo");
    return <Navigate to={returnTo || "/itinerary"} replace />;
  }

  return children;
};



// Animated Routes wrapper component
const AnimatedRoutes = () => {
  const location = useLocation();
  
  // Sync wishlist from MongoDB when user logs in
  useWishlistSync();

  // GA4 pageviews. Routes here are lazy-loaded under React Router, so GA's own
  // page_view fires only on the initial hard load — every in-app navigation has
  // to be reported explicitly or it is invisible. analytics.js disables the
  // automatic one so these are not double-counted.
  const lastPath = useRef(null);
  useEffect(() => {
    const path = location.pathname + location.search;
    // StrictMode double-invokes effects in development, and a re-render with an
    // unchanged location would otherwise report the same view twice.
    if (lastPath.current === path) return;
    lastPath.current = path;
    trackPageView(path);
  }, [location.pathname, location.search]);

  return (
    <PageTransition>
      {/* Sets per-route title, description, canonical and robots tags. */}
      <SeoManager />
      <Suspense fallback={<LoadingScreen />}>
        <Routes location={location}>
          {/* Public Route - Always accessible */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/book" element={<BookTrip />} />
          <Route path="/treks" element={<TreksPage />} />
          <Route path="/treks/:id" element={<TrekDetailPage />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/demo/:destination" element={<DemoItinerary />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<ContactUs />} />

          {/* SSO Callback - Handles OAuth redirects from Google, etc. */}
          <Route path="/sso-callback" element={<SSOCallback />} />
          
          {/* Agent J Route */}
          <Route path="/agent" element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />

          {/* Reel to Real Route */}
          <Route path="/reel-to-real" element={<ProtectedRoute><ReelToReal /></ProtectedRoute>} />

          {/* Budget Itinerary Route */}
          <Route path="/budget-itinerary" element={<ProtectedRoute><BudgetItineraryPage /></ProtectedRoute>} />


          {/* Fully public: a shared itinerary link opens read-only for anyone,
              signed in or not. Never wrap this in ProtectedRoute — the whole
              point is that the recipient needs no Journim account. */}
          <Route path="/i/:shareId" element={<SharedItinerary />} />

          {/* Semi-Public Routes - Accessible without login, but login required for final action */}
          <Route path="/itinerary" element={<Itinerary />} />
          <Route path="/choose-cluster/:state" element={<ChooseCluster />} />
          <Route path="/choose-destination" element={<ChooseDestination />} />
          <Route path="/choose-places" element={<ChoosePlaces />} />
          <Route path="/choose-hotel" element={<ChooseHotel />} />
          
          {/* Protected Routes - Only accessible when logged in */}
          <Route path="/wishlist" element={<ProtectedRoute><WishList /></ProtectedRoute>} />
          <Route path="/yourtrips" element={<ProtectedRoute><YourTrips /></ProtectedRoute>} />
          <Route path="/final-itinerary" element={<ProtectedRoute><FinalItinerary /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          
          {/* Auth Routes - Only accessible when NOT logged in */}
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/signup" element={<PublicOnlyRoute><SignUp /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />

          {/* Anything unmatched. A SPA still answers 200 here, so SeoManager
              marks these noindex — this route is what the visitor sees. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </PageTransition>
  );
};

function App() {
  return (
    <UserProvider>
      <Router>
        <Toaster 
          position="top-center"
          reverseOrder={false}
          gutter={12}
          containerStyle={{ zIndex: 99999 }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1a1a2e',
              color: '#fff',
              padding: '16px 24px',
              borderRadius: '12px',
              fontSize: '14px',
              maxWidth: '400px',
            },
            success: {
              iconTheme: {
                primary: '#2ec3d6',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff6b6b',
                secondary: '#fff',
              },
            },
          }}
        />
        <AnimatedRoutes />
      </Router>
    </UserProvider>
  );
}

export default App;
